import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Select, DatePicker, Input, message, InputNumber, Radio, Modal, Alert } from 'antd';
import dayjs from 'dayjs';
import { LeftOutlined, UserAddOutlined, TeamOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatGapWhInventory, useGetMatWarehouses, useGetCompanies } from '../../../hooks/genericHooks';
import type { VwMatGapWhInventory, MatWarehouses, Company } from '../../../types';
import { getTableNextSequenceValue, insertData } from '../../../services/api';
import { useAuth } from '../../../components/common/security/AuthContext';
import PersonnelSelect from '../../../components/modals/PersonnelSelect';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

// Local row type with stable key for selection/basket
 type InventoryRow = VwMatGapWhInventory & { key: string };

type AssigneeKind = 'person' | 'company';

export default function MatGapTxnAssign() {
  const navigate = useNavigate();

  // Data
  const { data: invData = [], isLoading, refetch: refetchInventory } = useGetMatGapWhInventory() as any;
  const { data: whData = [] } = useGetMatWarehouses() as any;
  const { data: companiesResp = [], isLoading: loadingCompanies } = useGetCompanies() as any;

  const inventory: VwMatGapWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);
  const companies: Company[] = Array.isArray(companiesResp) ? companiesResp : (companiesResp?.data ?? []);
  const { user: currentUser } = useAuth();

  // Cached maps/options
  const ownedWhSet = useMemo(() => {
    const s = new Set<number>();
    (warehouses || []).forEach(w => {
      const owners: string[] = Array.isArray(w?.owners) ? (w.owners as string[]) : [];
      if (currentUser && owners.includes(String(currentUser))) s.add(Number(w.id));
    });
    return s;
  }, [warehouses, currentUser]);
  const whOptions = useMemo(() => (warehouses || []).map(w => ({ value: w.id, label: w.name, disabled: !ownedWhSet.has(Number(w.id)) })), [warehouses, ownedWhSet]);
  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach(w => m.set(w.id, w.name));
    return m;
  }, [warehouses]);

  const companyOptions = useMemo(() => (companies || []).map(c => ({ value: c.id, label: c.name || String(c.id) })), [companies]);

  // Controls
  const [fromWarehouseId, setFromWarehouseId] = useState<number | undefined>(undefined);
  const [date, setDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [search, setSearch] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [extGroupFilter, setExtGroupFilter] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Assignee
  const [assigneeKind, setAssigneeKind] = useState<AssigneeKind>('person');
  const [personnelOpen, setPersonnelOpen] = useState(false);
  const [assigneeCode, setAssigneeCode] = useState<string | null>(null); // personnel code when assigneeKind==='person'
  const [assigneeName, setAssigneeName] = useState<string>(''); // display: "CODE - Name"
  // Recipient (real person who takes over) – store code and name
  const [recipientOpen, setRecipientOpen] = useState(false);
  const [recipientCode, setRecipientCode] = useState<string | null>(null);
  const [recipientName, setRecipientName] = useState<string>('');

  // For company select
  const [companyId, setCompanyId] = useState<number | undefined>(undefined);

  // Basket (key -> row + qty)
  const [basket, setBasket] = useState<Record<string, { row: InventoryRow; qty: number }>>({});
  const [submitting, setSubmitting] = useState(false);
  const isOwner = React.useMemo(() => {
    if (!fromWarehouseId || !currentUser) return false;
    const wh = (warehouses || []).find(w => Number(w.id) === Number(fromWarehouseId));
    const owners: string[] = Array.isArray(wh?.owners) ? (wh?.owners as string[]) : [];
    return owners.includes(String(currentUser));
  }, [fromWarehouseId, currentUser, warehouses]);
  const canTransact = Boolean(fromWarehouseId && currentUser && isOwner);

  // Filtering helpers
  const filtered: InventoryRow[] = useMemo(() => {
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);

    const matchesSearch = (r: VwMatGapWhInventory) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.mat_id} ${r.description || ''} ${r.unit || ''} ${r.prod_group || ''} ${r.hierarchy_long || ''} ${r.ext_group || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };

    return (inventory || [])
      .filter(r => (fromWarehouseId == null || r.warehouse_id === fromWarehouseId))
      .filter(r => (!groupFilter || String(r.prod_group || '').trim().toLowerCase() === String(groupFilter).trim().toLowerCase()))
      .filter(r => (!extGroupFilter || String(r.ext_group || '').trim().toLowerCase() === String(extGroupFilter).trim().toLowerCase()))
      .filter(r => Number(r.onhand_qty || 0) > 0)
      .filter(matchesSearch)
      .map(r => ({ ...r, key: `${r.mat_id}-${r.warehouse_id}` }));
  }, [inventory, fromWarehouseId, search, groupFilter, extGroupFilter]);

  const rowsByKey = useMemo(() => {
    const m: Record<string, InventoryRow> = {};
    filtered.forEach(r => { m[r.key] = r; });
    return m;
  }, [filtered]);

  const selectedKeys: React.Key[] = useMemo(() => {
    const keys = new Set(Object.keys(basket));
    return (filtered || []).filter(r => keys.has(r.key)).map(r => r.key);
  }, [basket, filtered]);

  const groupOptions = useMemo(() => {
    const norm = (s: any) => (s == null ? '' : String(s)).trim();
    const byKey = new Map<string, string>();
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const matchesSearch = (r: VwMatGapWhInventory) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.mat_id} ${r.description || ''} ${r.unit || ''} ${r.prod_group || ''} ${r.hierarchy_long || ''} ${r.ext_group || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };
    (inventory || [])
      .filter(r => fromWarehouseId == null || r.warehouse_id === fromWarehouseId)
      .filter(r => !extGroupFilter || norm(r.ext_group).toLowerCase() === norm(extGroupFilter).toLowerCase())
      .filter(matchesSearch)
      .forEach(r => {
        const raw = norm(r.prod_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [inventory, fromWarehouseId, extGroupFilter]);

  const extGroupOptions = useMemo(() => {
    const norm = (s: any) => (s == null ? '' : String(s)).trim();
    const byKey = new Map<string, string>();
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const matchesSearch = (r: VwMatGapWhInventory) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.mat_id} ${r.description || ''} ${r.unit || ''} ${r.prod_group || ''} ${r.hierarchy_long || ''} ${r.ext_group || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };
    (inventory || [])
      .filter(r => fromWarehouseId == null || r.warehouse_id === fromWarehouseId)
      .filter(r => !groupFilter || norm(r.prod_group).toLowerCase() === norm(groupFilter).toLowerCase())
      .filter(matchesSearch)
      .forEach(r => {
        const raw = norm(r.ext_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [inventory, fromWarehouseId, groupFilter]);

  // Auto-clear invalid selections when options change
  React.useEffect(() => {
    if (groupFilter && !groupOptions.some(o => (o.value as string) === groupFilter)) {
      setGroupFilter('');
    }
  }, [groupOptions, groupFilter]);
  React.useEffect(() => {
    if (extGroupFilter && !extGroupOptions.some(o => (o.value as string) === extGroupFilter)) {
      setExtGroupFilter('');
    }
  }, [extGroupOptions, extGroupFilter]);

  // Columns
  const columns = useMemo(() => [
    { title: 'Material ID', dataIndex: 'mat_id', key: 'mat_id', width: 130, sorter: (a: InventoryRow, b: InventoryRow) => String(a.mat_id || '').localeCompare(String(b.mat_id || '')) },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 90 },
    { title: 'Group', dataIndex: 'prod_group', key: 'prod_group', width: 160 },
    { title: 'Hierarchy (Long)', dataIndex: 'hierarchy_long', key: 'hierarchy_long', width: 220, ellipsis: true },
    { title: 'Ext Group', dataIndex: 'ext_group', key: 'ext_group', width: 140 },
    { title: 'From WH', dataIndex: 'warehouse_id', key: 'warehouse_id', width: 160, render: (v: number) => whMap.get(v) || v },
    { title: 'On-hand', dataIndex: 'onhand_qty', key: 'onhand_qty', width: 110, align: 'right', sorter: (a: InventoryRow, b: InventoryRow) => (a.onhand_qty ?? 0) - (b.onhand_qty ?? 0) },
    { title: 'Assign Qty', key: 'assign_qty', width: 160, render: (_: any, row: InventoryRow) => {
        const current = basket[row.key]?.qty ?? 0;
        return (
          <InputNumber
            min={0}
            max={row.onhand_qty}
            value={current}
            onChange={(v) => {
              const qty = typeof v === 'number' ? v : 0;
              setBasket(prev => {
                const next = { ...prev };
                if (qty > 0) {
                  next[row.key] = { row, qty };
                } else {
                  delete next[row.key];
                }
                return next;
              });
            }}
            style={{ width: 140 }}
            disabled={!canTransact}
          />
        );
      }
    },
  ], [basket, whMap]);

  const basketItems = useMemo(() => Object.values(basket), [basket]);
  const selectedCount = useMemo(() => basketItems.length, [basketItems]);

  // Extract personnel code from display like "CODE - Name" as fallback
  const parsePersonnelCodeFromDisplay = (display: string): string | null => {
    const m = (display || '').match(/^\s*([^\s-]+)\s*-/);
    return m ? m[1] : null;
  };

  const handleSubmit = async () => {
    try {
      if (!date) { message.warning('Please select Assign Date'); return; }
      if (!canTransact) { message.error('You are not an owner of the selected warehouse. Assign is restricted.'); return; }
      // Determine assignee string code per requirements
      const companyShortCode = assigneeKind === 'company' ? (companies || []).find((c: any) => c.id === companyId)?.short_code : null;
      const effectiveAssigneeCode = assigneeKind === 'person' ? (assigneeCode ?? parsePersonnelCodeFromDisplay(assigneeName)) : companyShortCode;
      if (assigneeKind === 'person') {
        if (!effectiveAssigneeCode) { message.warning('Please select Personnel'); return; }
      } else {
        if (!effectiveAssigneeCode) { message.warning('Please select Company'); return; }
      }
      if (!recipientCode) { message.warning('Please select Recipient (personnel code)'); return; }
      if (basketItems.length === 0) { message.warning('Please select at least one material and quantity'); return; }
      if (basketItems.some(x => !x.qty || x.qty <= 0)) { message.warning('Quantities must be > 0'); return; }
      const lines = basketItems;
      setSubmitting(true);

      // Use table sequence to build friendly reference_no (ASN-XXXXXXX)
      const nextId = await getTableNextSequenceValue('mat_gap_txns', 'id');
      if (!nextId) throw new Error('Failed to get next transaction id');
      const referenceNo = `ASN-${String(nextId).padStart(8, '0')}`;

      const payload = lines.map(({ row, qty }) => ({
        op_type: 'assign',
        txn_date: date, // YYYY-MM-DD
        mat_id: row.mat_id,
        qty,
        unit: row.unit,
        from_warehouse_id: row.warehouse_id,
        to_warehouse_id: null,
        assignee_type: assigneeKind,
        assignee_id: effectiveAssigneeCode as any, // backend expects varchar(50)
        reference_no: referenceNo,
        notes: notes || null,
        issued_to: recipientCode || null,
      }));

      await insertData('mat_gap_txns', payload);

      message.success(`Assigned ${payload.length} line(s). Ref: ${referenceNo}`);
      setBasket({});
      setNotes('');
      if (assigneeKind === 'company') {
        // keep company selection; clear any stale personnel display
        setAssigneeCode(null);
        setAssigneeName('');
      }
      if (typeof refetchInventory === 'function') await refetchInventory();
    } catch (err: any) {
      console.error(err);
      message.error(err?.message || 'Failed to create assignment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Assign Material</Title>
              <Text type="secondary">Select lines and quantities, then assign to Personnel or Company</Text>
            </div>
          </Space>
        </Col>
      </Row>

      {/* Filters */}
      <Row justify="end" style={{ marginBottom: 8 }}>
        <Col>
          <Space wrap>
            <Select
              placeholder="From Warehouse"
              allowClear
              value={fromWarehouseId}
              onChange={(v) => setFromWarehouseId(v)}
              options={whOptions}
              style={{ width: 220 }}
            />
            <Select
              placeholder="Group"
              allowClear
              showSearch
              value={groupFilter || undefined}
              onChange={(v) => setGroupFilter(v || '')}
              options={groupOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Ext Group"
              allowClear
              showSearch
              value={extGroupFilter || undefined}
              onChange={(v) => setExtGroupFilter(v || '')}
              options={extGroupOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              style={{ width: 200 }}
            />
            <Input
              placeholder="Search material/description"
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 240 }}
            />
          </Space>
        </Col>
      </Row>

      {/* Assignee, Recipient and Date */}
      <Row justify="space-between" style={{ marginBottom: 12 }}>
        <Col>
          <Space wrap>
            <Radio.Group
              value={assigneeKind}
              onChange={(e) => {
                const val = e.target.value as AssigneeKind;
                setAssigneeKind(val);
                // reset fields when switching
                setAssigneeCode(null);
                setAssigneeName('');
                setCompanyId(undefined);
              }}
            >
              <Radio.Button value="person">Personnel</Radio.Button>
              <Radio.Button value="company">Company</Radio.Button>
            </Radio.Group>

            {assigneeKind === 'person' ? (
              <Input
                placeholder="Select Personnel"
                value={assigneeName}
                readOnly
                style={{ width: 260 }}
                suffix={<Button size="small" icon={<UserAddOutlined />} onClick={() => setPersonnelOpen(true)} />}
              />
            ) : (
              <Select
                showSearch
                placeholder="Select Company"
                loading={loadingCompanies}
                value={companyId}
                onChange={(v) => setCompanyId(v)}
                options={companyOptions}
                style={{ width: 280 }}
                allowClear
                filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              />
            )}

            {/* Recipient person (real taker) */}
            <Input
              placeholder="Recipient (Personnel)"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              style={{ width: 280 }}
              suffix={<Button size="small" icon={<UserAddOutlined />} onClick={() => setRecipientOpen(true)} />}
            />

            <span>
              <Text strong>Assign Date</Text> <Text type="danger">*</Text>{' '}
              <DatePicker
                value={date ? dayjs(date) : null}
                onChange={(d) => setDate(d ? d.format('YYYY-MM-DD') : null)}
                style={{ width: 160 }}
                disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')}
              />
            </span>

            <Button action="add" type="primary" onClick={handleSubmit} loading={submitting} disabled={!canTransact}>
              {`Assign (${selectedCount || 0})`}
            </Button>
            <Button onClick={() => { setFromWarehouseId(undefined); setDate(dayjs().format('YYYY-MM-DD')); setSearch(''); setGroupFilter(''); setExtGroupFilter(''); }}>
              Reset
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Notes */}
      <Row style={{ marginBottom: 8 }}>
        <Col span={24}>
          <Input.TextArea
            placeholder="Assignment notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{ width: '100%' }}
            allowClear
          />
        </Col>
      </Row>

      {/* Basket Summary */}
      <Row style={{ marginTop: 12 }}>
        <Col span={24}>
          <Card
            size="small"
            title={
              <span>
                <ShoppingCartOutlined style={{ marginRight: 6 }} />
                {`Basket (${selectedCount})`}
              </span>
            }
            bodyStyle={{ padding: 8, background: '#fffbe6' }}
          >
            {basketItems.length === 0 ? (
              <Text type="secondary">No items in basket. Select rows and set quantities to add.</Text>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Mat ID</th>
                      <th style={{ textAlign: 'left' }}>Description</th>
                      <th style={{ textAlign: 'left' }}>Unit</th>
                      <th style={{ textAlign: 'left' }}>Warehouse</th>
                      <th style={{ textAlign: 'right' }}>Qty</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {basketItems.map(({ row, qty }) => (
                      <tr key={row.key}>
                        <td>{row.mat_id}</td>
                        <td style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description}</td>
                        <td>{row.unit}</td>
                        <td>{whMap.get(row.warehouse_id) || row.warehouse_id}</td>
                        <td style={{ textAlign: 'right' }}>
                          <InputNumber
                            size="small"
                            min={0}
                            max={row.onhand_qty}
                            value={qty}
                            onChange={(v) => {
                              const newQty = typeof v === 'number' ? v : 0;
                              setBasket(prev => {
                                const next = { ...prev } as Record<string, { row: InventoryRow; qty: number }>;
                                if (newQty > 0) {
                                  next[row.key] = { row, qty: newQty };
                                } else {
                                  delete next[row.key];
                                }
                                return next;
                              });
                            }}
                            style={{ width: 100 }}
                            disabled={!canTransact}
                          />
                        </td>
                        <td>
                          <Button
                            size="small"
                            danger
                            icon={<ShoppingCartOutlined />}
                            onClick={() =>
                              setBasket(prev => { const n = { ...prev }; delete n[row.key]; return n; })
                            }
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <StandardTable<InventoryRow>
        columns={columns as any[]}
        dataSource={filtered}
        rowKey={(r) => r.key}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys, rows) => {
            const want = new Set((keys || []).map(k => String(k)));
            setBasket(prev => {
              const next = { ...prev } as Record<string, { row: InventoryRow; qty: number }>;
              (filtered || []).forEach(r => {
                const k = String(r.key);
                if (want.has(k)) {
                  if (!next[k]) next[k] = { row: r, qty: Number(r.onhand_qty) || 0 };
                } else {
                  if (next[k]) delete next[k];
                }
              });
              return next;
            });
          },
          preserveSelectedRowKeys: true,
          getCheckboxProps: (record) => ({ disabled: !fromWarehouseId || !canTransact || record.warehouse_id !== fromWarehouseId || (record.onhand_qty || 0) <= 0 }),
        }}
        scroll={{ x: 'max-content' }}
      />

      {/* Personnel modal */}
      <PersonnelSelect
        open={personnelOpen}
        onCancel={() => setPersonnelOpen(false)}
        mode="both"
        onOk={(payload: any) => {
          // payload is [combined, codeStr, fullName] when mode="both"
          let codeStr: string | undefined;
          let fullName: string | undefined;
          let combined: string | undefined;
          if (Array.isArray(payload)) {
            [combined, codeStr, fullName] = payload as [string, string, string];
          } else if (typeof payload === 'string') {
            // Fallback: try to parse "<code> - <name>"
            const m = payload.match(/^\s*(\d+)\s*-\s*(.*)$/);
            if (m) {
              codeStr = m[1];
              fullName = m[2];
              combined = payload;
            } else {
              fullName = payload;
              combined = payload;
            }
          }
          if (codeStr) setAssigneeCode(codeStr);
          if (combined) setAssigneeName(combined);
          setPersonnelOpen(false);
        }}
      />

      {/* Recipient modal */}
      <PersonnelSelect
        open={recipientOpen}
        onCancel={() => setRecipientOpen(false)}
        mode="both"
        onOk={(payload: any) => {
          let codeStr: string | undefined;
          let fullName: string | undefined;
          if (Array.isArray(payload)) {
            [, codeStr, fullName] = payload as [string, string, string];
          } else if (typeof payload === 'string') {
            const m = payload.match(/^\s*(\d+)\s*-\s*(.*)$/);
            if (m) {
              codeStr = m[1];
              fullName = m[2];
            } else {
              fullName = payload;
            }
          }
          if (codeStr) setRecipientCode(codeStr);
          if (fullName) setRecipientName(fullName);
          setRecipientOpen(false);
        }}
      />
    </Card>
  );
}
